import { useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  PRE_ACTIVATION_AGENCY_SCHEDULE_BUTTON,
  PRE_ACTIVATION_AGENCY_SCHEDULE_LOCKED_HINT,
  PRE_ACTIVATION_SCHEDULED_SESSION_MISSING,
} from "../../../i18n/keysTranslations";
import Button from "../../../components/Buttons/Button";
import FieldError from "../components/FieldError";
import ModalPreActivationOnboarding from "../components/ModalPreActivationOnboarding";
import ScheduledSessionSummary from "../components/ScheduledSessionSummary";
import { createEmptyOnboardingSession } from "../agencyFormConfig";
import { normalizePreActivationWebsiteUrl } from "../preActivationFormValues";
import { isPreActivationFieldErrorVisible } from "../preActivationVisibleErrors";

const AgencyScheduleSection = ({
  setValue,
  canSchedule = false,
  readOnly = false,
  onboardingSession,
  fullName,
  email,
  phone,
  websiteUrl,
  scheduleError,
}) => {
  const { t } = useTranslation();
  const { formState } = useFormContext();
  const [modalOpen, setModalOpen] = useState(false);
  const hasBooking = Boolean(
    typeof onboardingSession?.startTime === "string" &&
      onboardingSession.startTime.trim()
  );
  const enabled = canSchedule && !readOnly && !hasBooking;

  const onCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const onBookingConfirmed = useCallback(
    (session) => {
      if (typeof setValue !== "function") return;
      setValue(
        "onboardingSession",
        {
          ...createEmptyOnboardingSession(),
          startTime:
            typeof session?.startTime === "string" ? session.startTime : "",
          endTime: typeof session?.endTime === "string" ? session.endTime : "",
          uid: typeof session?.uid === "string" ? session.uid : "",
          title: typeof session?.title === "string" ? session.title : "",
        },
        { shouldValidate: true, shouldDirty: true }
      );
    },
    [setValue]
  );

  if (hasBooking) {
    return <ScheduledSessionSummary session={onboardingSession} />;
  }

  if (readOnly) {
    return (
      <Typography
        sx={{
          fontSize: "13px",
          color: "text.secondary",
          lineHeight: 1.5,
        }}
      >
        {t(PRE_ACTIVATION_SCHEDULED_SESSION_MISSING)}
      </Typography>
    );
  }

  return (
    <Box
      id="onboardingSession.startTime"
      data-field="onboardingSession.startTime"
    >
      <Button
        type="button"
        fullWidth
        color="primary"
        disabled={!enabled}
        onClick={() => setModalOpen(true)}
        sx={{
          mt: 1,
          py: 1.5,
          fontWeight: 600,
          borderRadius: "9px !important",
        }}
      >
        {t(PRE_ACTIVATION_AGENCY_SCHEDULE_BUTTON)}
      </Button>
      {!canSchedule ? (
        <Typography
          role="status"
          sx={{
            mt: 0.5,
            fontSize: "11px",
            color: "text.secondary",
            textAlign: "left",
            lineHeight: 1.4,
          }}
        >
          {t(PRE_ACTIVATION_AGENCY_SCHEDULE_LOCKED_HINT)}
        </Typography>
      ) : null}
      <FieldError
        message={
          isPreActivationFieldErrorVisible({ formState }) ? scheduleError : ""
        }
      />
      <ModalPreActivationOnboarding
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        prefill={{
          name: fullName,
          email,
          phone,
          website: normalizePreActivationWebsiteUrl(websiteUrl),
        }}
        onBookingConfirmed={onBookingConfirmed}
      />
    </Box>
  );
};

export default AgencyScheduleSection;
